'use client'

import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import BedOutlinedIcon from '@mui/icons-material/BedOutlined'
import { Box, MenuItem } from '@mui/material'
import React from 'react'

import type { HotelDestinationSuggestion } from '../searchForm/hooks/useHotelSearchManager'

export interface DestinationSuggestionRow {
    suggestion: HotelDestinationSuggestion
    /** "2,993 Hotels" for a city, empty for a property. */
    countLabel: string
}

export interface DestinationSuggestionListProps {
    rows: DestinationSuggestionRow[]
    onSelect: (suggestion: HotelDestinationSuggestion) => void
}

/**
 * Rows mirror the flights predictive list (`searchModifierInputs/fromToInput`): leading
 * icon, two-line label, trailing grey pill, and no heading above the results. The pill is
 * width-free here because a property count is longer than an airport code.
 */
const DestinationSuggestionList: React.FC<DestinationSuggestionListProps> = ({ rows, onSelect }) => (
    <Box>
        {rows.map(({ suggestion, countLabel }) => (
            <MenuItem
                key={suggestion.key}
                onMouseDown={event => event.stopPropagation()}
                onClick={event => {
                    event.stopPropagation()
                    onSelect(suggestion)
                }}
                sx={{
                    borderBottom: '1px solid',
                    borderColor: 'borders.default',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    px: 1.5,
                    py: 1.2
                }}
            >
                <Box sx={{ minWidth: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {suggestion.kind === 'city' ? (
                        <ApartmentOutlinedIcon sx={{ fontSize: 28, color: 'grey.400' }} />
                    ) : (
                        <BedOutlinedIcon sx={{ fontSize: 28, color: 'grey.400' }} />
                    )}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                        fontWeight={600}
                        sx={{
                            fontSize: 14,
                            lineHeight: 1.5,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        {suggestion.primary}
                    </Box>
                    {suggestion.secondary && (
                        <Box
                            color="grey.600"
                            fontSize="12px"
                            sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                            {suggestion.secondary}
                        </Box>
                    )}
                </Box>

                {countLabel && (
                    <Box
                        sx={{
                            bgcolor: 'neutral.100',
                            color: 'textColors.primary',
                            borderRadius: 1,
                            fontWeight: 600,
                            fontSize: '12px',
                            px: 1.5,
                            py: 0.5,
                            ml: 2,
                            textAlign: 'center',
                            whiteSpace: 'nowrap',
                            flexShrink: 0
                        }}
                    >
                        {countLabel}
                    </Box>
                )}
            </MenuItem>
        ))}
    </Box>
)

export { DestinationSuggestionList }
export default DestinationSuggestionList
